import React, { useEffect, useMemo, useState } from 'react'
import DoneIcon from '@mui/icons-material/Done'
import {
  Box,
  Button,
  FormControl,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import ColoredHeaderCard from 'common/Card/ColoredHeaderCard'
import LeftIconHList from 'common/List/LeftIconHList'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import numeral from 'numeral'
import { IBundlePlans } from 'types/api/v1/bundle.type'
import { IVoucherBundles } from 'types/api/v1/voucher.type'

import {
  calculateOriginalBeforeDiscount,
  calculateTotalAmtAfterMoqDiscount,
  calculateTotalOriginal,
  getDiscountPerSim
} from '../../../../helpers/getDiscount'

interface SelectedPlanProps {
  simsQuantity: number | Array<number>
  selectedBundlePlan: IBundlePlans
  voucherData: IVoucherBundles[]
  setVoucherApplied: (value: IVoucherBundles | null) => void
  voucherApplied: IVoucherBundles | null
  setDiscountAmount: (value: number) => void
  discountAmount: number
  setFinalAmount: (value: number) => void
}

function PlanDetailsCard({
  simsQuantity,
  selectedBundlePlan,
  voucherData,
  setVoucherApplied,
  voucherApplied,
  setDiscountAmount,
  discountAmount,
  setFinalAmount
}: SelectedPlanProps) {
  const { category, description, voicePool, sms, data, price, moq } =
    selectedBundlePlan

  //valid,invalid, noPromoCode
  const [NO_PROMO_CODE, INVALID_CODE, VALID_CODE] = [
    'no promo code',
    'invalid',
    'valid'
  ]
  const DISCOUNT10_PROMOCODE = 'discount10'
  //if bundle plan selected is not circles3 and chatter id, apply discount
  const CIRCLES3_CHATTER_ID = '06c0b85c-0917-4ae5-b409-e46f46f7758a'
  const MINI_ID = '294aa12b-916c-439b-838e-751526872e0a'

  const [validPromoCode, setPromoCode] = useState(NO_PROMO_CODE)
  const [voucherCodeKeyed, setVoucherKeyed] = useState('')

  const checkVoucherCode = () => {
    const foundVoucher = voucherData.filter(
      (voucher) => voucher.code.toLowerCase() === voucherCodeKeyed
    )
    if (
      selectedBundlePlan.bundleId !== CIRCLES3_CHATTER_ID &&
      selectedBundlePlan.bundleId !== MINI_ID
    ) {
      if (foundVoucher.length > 0) {
        setVoucherApplied(foundVoucher[0])
        setPromoCode(VALID_CODE)
        setDiscountAmount(Number(foundVoucher[0].discountAmt))
      } else {
        setPromoCode(INVALID_CODE)
        setVoucherKeyed('')
      }
    }
  }

  useEffect(() => {
    if (
      selectedBundlePlan.bundleId !== CIRCLES3_CHATTER_ID &&
      selectedBundlePlan.bundleId !== MINI_ID
    ) {
      setPromoCode(VALID_CODE)
      setVoucherKeyed(DISCOUNT10_PROMOCODE)
      const foundVoucher = voucherData.filter(
        (voucher) => voucher.code.toLowerCase() === DISCOUNT10_PROMOCODE
      )
      if (foundVoucher.length > 0) {
        setVoucherApplied(foundVoucher[0])
        setDiscountAmount(Number(foundVoucher[0].discountAmt))
        setPromoCode(VALID_CODE)
      }
    } else {
      setPromoCode(NO_PROMO_CODE)
      setDiscountAmount(0)
      setVoucherKeyed('')
    }
  }, [selectedBundlePlan])

  const removeVoucherCode = () => {
    setVoucherApplied(null)
    setVoucherKeyed('')
    setPromoCode(NO_PROMO_CODE)
    setDiscountAmount(0)
  }

  //get calculated discount per sim
  const getCalculatedDiscountSim = (moqAmt, quantitySim) => {
    const convertedSimQty = Number(quantitySim)
    return Number(getDiscountPerSim(moqAmt, convertedSimQty))
  }

  const calculatedDiscountPerSim = useMemo(
    () => getCalculatedDiscountSim(moq, simsQuantity),
    [simsQuantity]
  )

  //to calculate total amount after discount applied
  const calculatedTotalAmount = useMemo(
    () =>
      calculateTotalAmtAfterMoqDiscount(
        calculateOriginalBeforeDiscount(
          Number(price.value),
          selectedBundlePlan,
          discountAmount
        ),
        Number(simsQuantity),
        calculatedDiscountPerSim
      ),
    [simsQuantity, selectedBundlePlan, discountAmount]
  )

  useEffect(() => {
    setFinalAmount(calculatedTotalAmount)
  }, [simsQuantity, selectedBundlePlan, discountAmount])

  return (
    <>
      <ColoredHeaderCard header={category}>
        <Stack>
          {/* title and text field */}
          <Box>
            <Box
              sx={{
                fontSize: '16px',
                marginBottom: 2,
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant='h4'
                sx={{
                  textTransform: 'capitalize'
                }}
              >
                {description}
              </Typography>
              <Typography
                variant='h4'
                sx={{
                  textTransform: 'uppercase'
                }}
              >
                {simsQuantity} Sims{' '}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1vw',
                marginBottom: '0.8vh'
              }}
            >
              <FormControl
                size='small'
                fullWidth
                disabled={voucherData.length === 0}
              >
                <TextField
                  disabled={validPromoCode === VALID_CODE}
                  data-cy='promoCodeInputField'
                  size='small'
                  inputProps={{
                    sx: {
                      color: 'primary.dark',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'uppercase'
                    }
                  }}
                  value={voucherCodeKeyed}
                  placeholder='PROMO CODE'
                  onChange={(e) => setVoucherKeyed(e.target.value)}
                />
              </FormControl>

              {validPromoCode === VALID_CODE ? (
                <Button
                  data-cy='changeVoucherBtn'
                  onClick={() => removeVoucherCode()}
                  variant='secondary'
                >
                  Change
                </Button>
              ) : (
                <Button
                  data-cy='applyVoucherBtn'
                  onClick={() => checkVoucherCode()}
                  variant='filled'
                >
                  Apply
                </Button>
              )}
            </Box>
          </Box>
          {/* show total cost */}
          <Box>
            {validPromoCode === INVALID_CODE && (
              <Typography
                sx={{
                  marginTop: '8px',
                  backgroundColor: 'note.light',
                  padding: '4px 8px'
                }}
                variant='h5'
              >
                No Promo Code Found
              </Typography>
            )}
            {validPromoCode === VALID_CODE && (
              <>
                <Box sx={{ marginTop: '8px' }}>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      backgroundColor: 'note.light',
                      padding: '4px 16px',
                      margin: '8px auto',
                      fontWeight: 600
                    }}
                  >
                    Promo code{' '}
                    <span
                      style={{
                        textTransform: 'uppercase',
                        color: circlesOrbitColorPalette.accent.secondary,
                        fontWeight: 600
                      }}
                    >
                      {voucherApplied?.code}
                    </span>{' '}
                    applied successfully
                  </Typography>
                </Box>
              </>
            )}
            {
              <Box>
                {simsQuantity > 0 && validPromoCode === VALID_CODE && (
                  <>
                    <Typography
                      sx={{
                        textDecoration: 'line-through',
                        display: 'inline-block',
                        color: 'neutrals.400'
                      }}
                    >
                      {price.symbol}
                      {Number.isInteger(
                        calculateTotalOriginal(simsQuantity, price.value)
                      )
                        ? calculateTotalOriginal(
                            simsQuantity,
                            Number(price.value)
                          )
                        : calculateTotalOriginal(
                            simsQuantity,
                            price.value
                          ).toFixed(2)}
                      /Month
                    </Typography>
                    {simsQuantity && calculatedDiscountPerSim < 0 && (
                      <Box
                        sx={{
                          display: 'inline-block',
                          backgroundColor: 'accent.secondary',
                          color: 'white',
                          marginLeft: '0.5vw',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 600
                        }}
                      >
                        -{price.symbol}
                        {Number.isInteger(Math.abs(calculatedDiscountPerSim))
                          ? Math.abs(calculatedDiscountPerSim)
                          : Math.abs(calculatedDiscountPerSim).toFixed(2)}
                        /SIM
                      </Box>
                    )}
                  </>
                )}
              </Box>
            }
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  color: 'primary.dark'
                }}
              >
                <Typography variant='h3' sx={{ color: 'primary.dark' }}>
                  {price.symbol}
                </Typography>
                <Typography variant='h3'>
                  {Number.isInteger(calculatedTotalAmount)
                    ? calculatedTotalAmount
                    : calculatedTotalAmount.toFixed(2)}
                </Typography>
                <Typography sx={{ color: 'primary.dark' }}>/Month</Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant='h6' sx={{ color: 'primary.dark' }}>
              Everything below is covered in this plan:
            </Typography>

            <LeftIconHList icon={<DoneIcon />}>
              {/* to change the data format */}
              <Box>{data ? numeral(Number(data) * 1000).format('0b') : ''}</Box>
            </LeftIconHList>
            <LeftIconHList icon={<DoneIcon />}>
              <Box>{voicePool} Mins Talktime</Box>
            </LeftIconHList>
            <LeftIconHList icon={<DoneIcon />}>
              <Box>{sms} SMS</Box>
            </LeftIconHList>
            {/* OTHER ADD ONS HARD CODED */}
            <LeftIconHList icon={<DoneIcon />}>
              <Box>Free caller ID</Box>
            </LeftIconHList>
            <LeftIconHList icon={<DoneIcon />}>
              <Box>No contract</Box>
            </LeftIconHList>
          </Box>
        </Stack>
      </ColoredHeaderCard>
    </>
  )
}

export default PlanDetailsCard
